import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { TakeoffData } from "@ServerTypes/takeoffs/takeoffs.schema";
import type { MeasurementItem } from "@ServerTypes/measurements/measurements.schema";

export interface MeasurementModalStatus {
  status: boolean;
}

const measurementsListSlice = createSlice({
  name: "measurementsList",
  initialState: [] as MeasurementItem[],
  reducers: {
    setMeasurementsData: (_state: MeasurementItem[], action: PayloadAction<MeasurementItem[] | undefined>) =>
      action.payload ?? [],
  },
});

/**
 * Holds the `getTakeoffsList` result, i.e. full `TakeoffData` rows (the response schema is
 * `z.array(takeoffData)`). It was previously typed `TakeoffListItemAttached[]` — the much
 * narrower `getTakeoffsListAttachedToItem` shape, which omits `createdBy`, `takeOffItemId`'s
 * `id` alias, styling and `automationQuery` — which forced every reader to re-assert the row
 * type and stripped the `ObjectIdString` brand off the ids it really carries.
 */
const takeoffsListSlice = createSlice({
  name: "takeoffsList",
  initialState: [] as TakeoffData[],
  reducers: {
    setTakeoffsList: (_state: TakeoffData[], action: PayloadAction<TakeoffData[] | undefined>) => action.payload ?? [],
  },
});

const takeoffDataSlice = createSlice({
  name: "takeoffData",
  initialState: {} as Partial<TakeoffData>,
  reducers: {
    setTakeoff: (_state: Partial<TakeoffData>, action: PayloadAction<Partial<TakeoffData>>) => action.payload,
  },
});

const measurementsModalStatusSlice = createSlice({
  name: "measurementsModalStatus",
  initialState: { status: false } as MeasurementModalStatus,
  reducers: {
    setMeasurementsModalStatus: (_state: MeasurementModalStatus, action: PayloadAction<MeasurementModalStatus>) =>
      action.payload,
  },
});

const isMeasurementStartedSlice = createSlice({
  name: "isMeasurementStarted",
  initialState: { status: false } as MeasurementModalStatus,
  reducers: {
    setIsMeasurementStarted: (_state: MeasurementModalStatus, action: PayloadAction<MeasurementModalStatus>) =>
      action.payload,
  },
});

const { setTakeoff: _setTakeoffRaw } = takeoffDataSlice.actions;
const { setMeasurementsModalStatus: _setMeasurementsModalStatusRaw } = measurementsModalStatusSlice.actions;
const { setIsMeasurementStarted: _setIsMeasurementStartedRaw } = isMeasurementStartedSlice.actions;

/**
 * `setMeasurementsData` and `setTakeoffsList` are exported raw — all call sites
 * pass API response data (already strictly typed by the response schema), and
 * `ExactArrayItems<>` produces false positives on widened response array types
 * since the source array doesn't bear the per-element Exact brand.
 */
export const { setMeasurementsData } = measurementsListSlice.actions;
export const { setTakeoffsList } = takeoffsListSlice.actions;

/**
 * Object-payload action creators are wrapped with `Exact<>` so excess properties are caught even when the
 * payload is passed through a variable.
 *
 * On `setTakeoff` the `Exact<>` is applied to the PARAMETER (`T & Exact<…>`) rather than used as the
 * constraint — the same form `contracts.reducer.ts` and `reducers/fileSync.ts` document. Under
 * `exactOptionalPropertyTypes` type-fest's `ExactObject` homomorphic mapping re-emits every optional member
 * as `k?: V | undefined`, so a bare `T extends Exact<Partial<TakeoffData>, T>` no longer proves `T` is a
 * `Partial<TakeoffData>`.
 */
export const setTakeoff = <T extends Partial<TakeoffData>>(payload: T & Exact<Partial<TakeoffData>, T>) =>
  _setTakeoffRaw(payload);
export const setMeasurementsModalStatus = <T extends Exact<MeasurementModalStatus, T>>(payload: T) =>
  _setMeasurementsModalStatusRaw(payload);
export const setIsMeasurementStarted = <T extends Exact<MeasurementModalStatus, T>>(payload: T) =>
  _setIsMeasurementStartedRaw(payload);

export const measurementsList = measurementsListSlice.reducer;
export const takeoffsList = takeoffsListSlice.reducer;
export const takeoffData = takeoffDataSlice.reducer;
export const measurementsModalStatus = measurementsModalStatusSlice.reducer;
export const isMeasurementStarted = isMeasurementStartedSlice.reducer;
